import { DialogModal } from '../../components';
import { UserRole } from '../../types/user.types';
import { metaTitle } from '../../util/tabTitle';
import { useIsUserRole } from '../../util/useIsUserRole';
import { AddAssetForm } from './AddAssetForm';
import { AssetsTable } from './AssetsTable';
import { DeleteUnusedAssetsDialog } from './DeleteUnusedAssetsDialog';
import {
  Button,
  Box,
  debounce,
  FormControlLabel,
  Stack,
  Checkbox,
} from '@mui/material';
import { ChangeEvent, FC, useState } from 'react';
import { DEBOUNCE_TIME } from '../../configs';
import Breadcrumbs from '../../components/Breadcrumb';
import { DeleteOutline } from '@mui/icons-material';
import {
  managementControlSx,
  ManagementPageHeader,
  ManagementSearch,
  ManagementToolbar,
  RefreshAction,
} from '../../components/ManagementPageLayout';

const AssetsPage: FC = () => {
  metaTitle('Assets');

  const [openAddAssetModal, setOpenAddAssetModal] = useState(false);
  const [openDeleteUnusedModal, setOpenDeleteUnusedModal] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const [assetSearchTxt, setAssetSearchTxt] = useState('');
  const [showImage, setShowImage] = useState(true);
  const [showVideo, setShowVideo] = useState(true);

  const disableUploadAssets = useIsUserRole([
    UserRole.user,
    UserRole.dataEditor,
  ]);

  const setSearchText = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setAssetSearchTxt(event.target.value);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          {
            label: 'Assets Library',
            to: '/assets',
            description:
              'This page serves as a central hub for all assets used in creating slides. Users can efficiently manage their assets here by adding or removing them. The page also offers filtering options specifically for images and videos.',
          },
        ]}
      />
      <ManagementPageHeader
        title="Media & assets"
        subtitle="Upload and manage media used across your slides."
        actionLabel="Upload assets"
        onAction={() => setOpenAddAssetModal(true)}
        actionDisabled={disableUploadAssets}
        secondaryAction={
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={() => setOpenDeleteUnusedModal(true)}
            disabled={disableUploadAssets}
            sx={{ ...managementControlSx, fontWeight: 600 }}
          >
            Delete unused
          </Button>
        }
      />
      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search assets"
          value={assetSearchTxt}
          onChange={debounce((event) => setSearchText(event), DEBOUNCE_TIME)}
        />
        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Checkbox
                checked={showImage}
                onChange={() => setShowImage(!showImage)}
                disabled={!showVideo}
                size="small"
              />
            }
            label="Images"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showVideo}
                onChange={() => setShowVideo(!showVideo)}
                disabled={!showImage}
                size="small"
              />
            }
            label="Videos"
          />
          <RefreshAction onClick={() => setRefreshTable(!refreshTable)} />
        </Stack>
      </ManagementToolbar>

      <AssetsTable
        refresh={refreshTable}
        assetSearchTxt={assetSearchTxt as string}
        showImage={showImage}
        showVideo={showVideo}
      />

      <DialogModal
        title={'Upload assets'}
        onClose={() => {
          setOpenAddAssetModal(false);
        }}
        open={openAddAssetModal}
        width={600}
      >
        <AddAssetForm
          onFinished={() => {
            setOpenAddAssetModal(false);
            setRefreshTable(!refreshTable);
          }}
        />
      </DialogModal>

      <DeleteUnusedAssetsDialog
        open={openDeleteUnusedModal}
        onClose={() => setOpenDeleteUnusedModal(false)}
        onDeleted={() => setRefreshTable(!refreshTable)}
      />
    </Box>
  );
};

export default AssetsPage;
